import type { CapopsComponent, CurvePhase, DtoBand, Logistics, MaintenanceColor, MaintenancePhase, MaterielFamily, UnitCapops } from "./api/types";
import { k, t } from "./i18n/i18n";

/** Maintenance (chantier « DTO / CAPOPS ») : vocabulaire fermé de l'interface, seuils de couleur partagés avec le
 *  serveur (`generex_engine/maintenance.py`) et phrases composées (« CAPOPS 62 % : limitée par la DTO des chars et
 *  chenillés »). Module pur : il ne fait que présenter ce que le serveur calcule (`Logistics.maintenance`).
 *
 *  DTO : disponibilité technique opérationnelle, part des matériels techniquement disponibles ; indicateur logistique,
 *  jugé contre la DTO attendue de chaque temps (fourchette), avec ses propres couleurs (`dtoColor`). CAPOPS : capacité
 *  opérationnelle, aptitude de l'unité à remplir sa mission (maillon le plus faible du personnel, des matériels et des
 *  munitions), aux couleurs de l'iPad (`maintenanceColor`). Les deux sigles restent à l'écran ; ils sont écrits en
 *  toutes lettres à leur première occurrence. */

/** Familles de matériel, dans l'ordre du serveur. */
export const FAMILIES: readonly MaterielFamily[] = ["tracked", "wheeled", "artillery", "engineer", "logistic", "drones"];

const FAMILY_LABELS: Record<MaterielFamily, string> = {
  tracked: k("Chars et chenillés"),
  wheeled: k("Blindés à roues"),
  artillery: k("Artillerie"),
  engineer: k("Génie"),
  logistic: k("Véhicules logistiques"),
  drones: k("Drones"),
};

/** Libellé d'une famille de matériel (« Chars et chenillés »). */
export function familyLabel(family: MaterielFamily): string {
  return t(FAMILY_LABELS[family]);
}

/** Sigles en toutes lettres, pour leur première occurrence dans une vue et l'aide. */
export const DTO_FULL = k("DTO (disponibilité technique opérationnelle)");
export const CAPOPS_FULL = k("CAPOPS (capacité opérationnelle)");

/** Temps de la courbe de DTO : T0 (départ, à l'heure H), puis la fin de chaque temps. */
export const CURVE_PHASES: readonly CurvePhase[] = ["T0", "T1", "T2", "T3"];

/** Seuils de couleur de la CAPOPS, comme l'iPad et le serveur : vert au-dessus de 80 %, rouge à 60 % et en dessous. */
export const GREEN_ABOVE = 80;
export const RED_AT_MOST = 60;
/** DTO : orange sous la borne basse de la DTO attendue de moins de 5 points, rouge plus bas (comme le serveur). */
export const DTO_ORANGE_POINTS = 5;

/** Couleur d'une CAPOPS arrondie à l'entier affiché ; null sans valeur. Jamais pour une DTO (`dtoColor`). */
export function maintenanceColor(percent: number | null | undefined): MaintenanceColor | null {
  if (percent == null || Number.isNaN(percent)) return null;
  const value = Math.round(percent);
  if (value > GREEN_ABOVE) return "green";
  if (value > RED_AT_MOST) return "orange";
  return "red";
}

/** Couleur d'une DTO face à la borne basse de la DTO attendue de son temps : conforme (vert) dans la fourchette ou
 *  au-dessus — jamais une alerte —, orange sous la borne de moins de 5 points, rouge plus bas ; null sans valeur. */
export function dtoColor(percent: number | null | undefined, low: number): MaintenanceColor | null {
  if (percent == null || Number.isNaN(percent)) return null;
  const gap = Math.round(low) - Math.round(percent);
  if (gap <= 0) return "green";
  return gap < DTO_ORANGE_POINTS ? "orange" : "red";
}

/** « 60 à 75 % » : fourchette de DTO attendue. */
export function bandText(band: DtoBand): string {
  return t("{low} à {high} %", { low: Math.round(band.low), high: Math.round(band.high) });
}

/** « DTO attendue en T2 : 60 à 75 % ». */
export function expectedText(phase: CurvePhase, band: DtoBand): string {
  return t("DTO attendue en {phase} : {band}", { phase, band: bandText(band) });
}

/** Lecture d'une DTO face à l'attendu : « conforme à l'attendu » (dans la fourchette ou au-dessus) ou « sous
 *  l'attendu ». */
export function dtoReading(percent: number, band: DtoBand): string {
  return dtoColor(percent, band.low) === "green" ? t("conforme à l'attendu") : t("sous l'attendu");
}

/** Ton des composants partagés (`Badge`, `Callout`) pour une couleur. */
export function colorTone(color: MaintenanceColor | null): "ok" | "warn" | "danger" | "neutral" {
  if (color === "green") return "ok";
  if (color === "orange") return "warn";
  if (color === "red") return "danger";
  return "neutral";
}

/** Code du point de situation DIREX (`DirexCell.color` : 0 vert, 1 orange, 2 rouge ; −1 non évaluée). */
export function cellColor(color: MaintenanceColor | null): -1 | 0 | 1 | 2 {
  return color === "green" ? 0 : color === "orange" ? 1 : color === "red" ? 2 : -1;
}

/** Pourcentage affiché (« 62 % »), arrondi à l'entier. */
export function percentText(percent: number): string {
  return t("{n} %", { n: Math.round(percent) });
}

const LIMITS: Record<Exclude<CapopsComponent, "materiel">, string> = {
  personnel: k("limitée par le personnel"),
  munitions: k("limitée par les munitions"),
};

/** « limitée par la DTO de… » : une phrase par famille (article et accord du français). */
const FAMILY_LIMITS: Record<MaterielFamily, string> = {
  tracked: k("limitée par la DTO des chars et chenillés"),
  wheeled: k("limitée par la DTO des blindés à roues"),
  artillery: k("limitée par la DTO de l'artillerie"),
  engineer: k("limitée par la DTO du génie"),
  logistic: k("limitée par la DTO des véhicules logistiques"),
  drones: k("limitée par la DTO des drones"),
};

/** « DTO des chars et chenillés » : une phrase par famille (article et accord du français). */
const FAMILY_DTO: Record<MaterielFamily, string> = {
  tracked: k("DTO des chars et chenillés"),
  wheeled: k("DTO des blindés à roues"),
  artillery: k("DTO de l'artillerie"),
  engineer: k("DTO du génie"),
  logistic: k("DTO des véhicules logistiques"),
  drones: k("DTO des drones"),
};

/** « DTO des chars et chenillés ». */
export function familyDto(family: MaterielFamily): string {
  return t(FAMILY_DTO[family]);
}

/** Ce qui limite une CAPOPS (« limitée par la DTO des chars et chenillés ») ; null quand rien ne la limite. */
export function limitText(capops: UnitCapops): string | null {
  if (!capops.limit) return null;
  if (capops.limit === "materiel") return capops.family ? t(FAMILY_LIMITS[capops.family]) : null;
  return t(LIMITS[capops.limit]);
}

/** Phrase complète : « CAPOPS 62 % : limitée par la DTO des chars et chenillés » (« CAPOPS 100 % » sans limite). */
export function capopsSentence(capops: UnitCapops): string {
  const head = t("CAPOPS {n} %", { n: Math.round(capops.capops) });
  const limit = limitText(capops);
  return limit ? t("{head} : {limit}", { head, limit }) : head;
}

/** CAPOPS d'une unité au temps affiché, lue dans la réponse de la logistique (la fiche d'unité partage sa requête) ;
 *  null en CENTAC, sans estimation ou pour une unité non posée. */
export function unitCapops(logistics: Logistics | undefined, key: string, phase: MaintenancePhase): UnitCapops | null {
  return logistics?.maintenance?.units[key]?.[phase] ?? null;
}
